import React from "react";
import { Box, IconButton, Typography, Popover, Button } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { darken, useTheme } from "@mui/material/styles";
// import useChatStore from "../../../../stores/chatStore";

const tagColors = [
  "#C8E6C9", // light green 1
  "#A5D6A7", // light green 2
  "#81C784", // light green 3
  "#66BB6A", // light green 4
  "#FFCDD2", // light red
  "#E1BEE7", // light purple
  "#BBDEFB", // light blue
  "#FFE0B2", // light orange
];

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

const getColorForTag = (tag: string): string => {
  const index = hashString(tag) % tagColors.length;
  return tagColors[index];
};

interface ReactionAndFavoriteProps {
  currentReaction?: string;
  markedIds: Record<string, string>;
  isSaved?: boolean;
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  reactionBg?: string;
  isTags?: string[];
}

export const ReactionAndFavorite: React.FC<ReactionAndFavoriteProps> = ({
  currentReaction,
  markedIds,
  isSaved,
  isTags = [],
  handleReactionClick,
  handleRemoveTag,
  handleAddToFavorite,
  reactionBg = "inherit",
}) => {
  // const { setCopyMessage } = useChatStore();
  const theme = useTheme();

  const [confirmAnchorEl, setConfirmAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [tagToRemove, setTagToRemove] = React.useState<string | null>(null);

  // const [, setAnchorEl] = React.useState<HTMLElement | null>(null);
  // const open = Boolean(anchorEl);

  // const [tagContextMenuAnchor, setTagContextMenuAnchor] =
  //   React.useState<HTMLElement | null>(null);
  // const [selectedTagForContextMenu, setSelectedTagForContextMenu] =
  //   React.useState<string | null>(null);
  // const longPressTimer = React.useRef<number | null>(null);

  // const handleTagOpen = (event: React.MouseEvent<HTMLElement>) => {
  //   setAnchorEl(event.currentTarget);
  // };

  // const handleTagClose = () => {
  //   setAnchorEl(null);
  // };

  // const handleTagContextMenuOpen = (
  //   event: React.MouseEvent<HTMLElement>,
  //   tag: string,
  // ) => {
  //   event.preventDefault();
  //   event.stopPropagation();
  //   setSelectedTagForContextMenu(tag);
  //   setTagContextMenuAnchor(event.currentTarget);
  // };

  // const handleTagContextMenuClose = () => {
  //   setTagContextMenuAnchor(null);
  //   setSelectedTagForContextMenu(null);
  // };

  const adjustedBg =
    reactionBg !== "inherit" && !reactionBg.startsWith("linear-gradient")
      ? darken(reactionBg, 0.3)
      : reactionBg;

  // const onTouchStart = (event: React.TouchEvent<HTMLElement>, tag: string) => {
  //   longPressTimer.current = window.setTimeout(() => {
  //     setSelectedTagForContextMenu(tag);
  //     setTagContextMenuAnchor(event.currentTarget);
  //   }, 600);
  // };

  // const onTouchEnd = () => {
  //   if (longPressTimer.current) {
  //     clearTimeout(longPressTimer.current);
  //     longPressTimer.current = null;
  //   }
  // };

  // const handleCopyTag = (tag: string, event: React.MouseEvent<HTMLElement>) => {
  //   event.stopPropagation();
  //   navigator.clipboard
  //     .writeText(tag)
  //     .then(() => {
  //       console.log(`Tag "${tag}" copied`);
  //       setCopyMessage("Content copied");
  //       handleTagClose();
  //     })
  //     .catch((err) => console.error("Failed to copy tag:", err));
  // };

  const handleConfirmRemoveTag = (
    tag: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setTagToRemove(tag);
    setConfirmAnchorEl(event.currentTarget);
  };

  const onConfirm = async () => {
    setConfirmAnchorEl(null);
    setTagToRemove(null);
    if (tagToRemove) {
      try {
        await handleRemoveTag(tagToRemove);
      } catch (error) {
        console.error("Error removing tag:", error);
      }
    }
  };

  const onCancel = () => {
    setTagToRemove(null);
    setConfirmAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mt: 1,
          alignItems: "center",
          width: "100%",
          minWidth: 0,
          flexWrap: "wrap",
        }}
      >
        {currentReaction && currentReaction !== "0" && (
          <IconButton
            onClick={() => handleReactionClick("0")}
            size="small"
            color="primary"
            sx={{
              px: "14px",
              py: "8px",
              background: adjustedBg,
              borderRadius: "16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              "&:hover": { background: adjustedBg },
            }}
          >
            <Box>{markedIds[currentReaction]}</Box>
          </IconButton>
        )}

        {isSaved && (
          <IconButton
            onClick={handleAddToFavorite}
            size="small"
            color="primary"
            sx={{
              px: "14px",
              py: "8px",
              background: adjustedBg,
              borderRadius: "16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              "&:hover": { background: adjustedBg },
            }}
          >
            <Box>⭐️</Box>
          </IconButton>
        )}

        {isTags.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              pr: { xs: 0.6 },
              maxWidth: "100%",
              overflow: "visible",
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            {isTags.map((tag) => {
              const displayTag =
                tag.length > 15 ? `${tag.slice(0, 15)}...` : tag;
              const tagColor = getColorForTag(tag);
              return (
                <Box
                  key={tag}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: tagColor,
                    borderRadius: 3,
                    px: 1.2,
                    py: 0.1,
                    fontSize: "10px",
                    fontWeight: 400,
                    color: theme.palette.getContrastText(tagColor),
                    whiteSpace: "nowrap",
                  }}
                >
                  <Tooltip title={tag}>
                    <Typography
                      sx={{
                        mr: 0.2,
                        fontSize: { xs: "9px", sm: "12px", md: "15px" },
                      }}
                    >
                      {displayTag}
                    </Typography>
                  </Tooltip>
                  <IconButton
                    onClick={(event) => {
                      if (tag === "SENT_FEEDBACK") {
                        handleRemoveTag(tag).catch(console.error);
                      } else {
                        handleConfirmRemoveTag(tag, event);
                      }
                    }}
                    size="small"
                    sx={{
                      p: 0,
                      color: theme.palette.getContrastText(tagColor),
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 0.7,
                      },
                    }}
                  >
                    ×
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Popover
        open={Boolean(confirmAnchorEl)}
        anchorEl={confirmAnchorEl}
        onClose={onCancel}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            p: 1,
            maxWidth: 200,
            borderRadius: 3,
            backgroundColor:
              theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.95)"
                : "rgba(30, 30, 30, 0.95)",
            backdropFilter: "blur(8px)",
            border:
              theme.palette.mode === "light"
                ? "1px solid #E6E6E6"
                : "1px solid #444",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            fontFamily: theme.typography.fontFamily,
          },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            mb: 1,
            textAlign: "center",
          }}
        >
          Confirm delete?
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            size="small"
            onClick={onCancel}
            sx={{
              fontSize: "0.75rem",
              px: 0.5,
              mr: 0.5,
              borderRadius: 3,
              textTransform: "none",
              color: theme.palette.text.secondary,
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onConfirm}
            sx={{
              fontSize: "0.75rem",
              px: 1,
              borderRadius: 3,
              textTransform: "none",
              bgcolor: theme.palette.mode === "light" ? "#7B49C1" : "#A28CCB",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor:
                  theme.palette.mode === "light"
                    ? darken("#7B49C1", 0.1)
                    : darken("#A28CCB", 0.1),
              },
            }}
          >
            Confirm
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default ReactionAndFavorite;
